import { api, publicApi } from '../lib/api'
import type {
  BusinessBoost,
  BusinessBookingItem,
  BusinessBookingNotification,
  BusinessBookingSettings,
  BusinessAdminSummary,
  BusinessActionsSummary,
  BusinessCallClickResponse,
  BusinessCallAdCampaign,
  BusinessCallAdsSummary,
  BusinessCallEventRow,
  BusinessCallsSummary,
  BusinessComment,
  BusinessCommentsResponse,
  BusinessFollowResponse,
  BusinessTeamMember,
  MeBusinessMembership,
  BusinessLikeResponse,
  BusinessMetricsSummary,
  BusinessOffer,
  BusinessReview,
  BusinessReviewsResponse,
  BusinessProfile,
  BusinessSaveResponse,
  BusinessShareResponse,
  UpsertBusinessOfferPayload,
  BusinessGiftSendPayload,
  BusinessGiftSendResponse,
  BusinessHoursGrouped,
  BusinessHoursManagePayload,
  BusinessSpecialHour,
  CallPricingTier,
  CallSettings,
  SponsoredLeadEvent,
  SponsoredCallAdBusiness,
  CreateBusinessBoostPayload,
  CreateBusinessPayload,
  AddTeamMemberPayload,
  UpsertBusinessCallAdCampaignPayload,
  UpdateCallSettingsPayload,
  UpdateBusinessPayload,
} from '../types/businesses'

type MineBusinessesResponse = {
  results: BusinessProfile[]
}

type BusinessBoostsResponse = {
  results: BusinessBoost[]
}

type BusinessCallEventsResponse = {
  results: BusinessCallEventRow[]
  page: number
  page_size: number
  has_next: boolean
  since_days: number
}

type SponsoredLeadEventsResponse = {
  results: SponsoredLeadEvent[]
  page: number
  page_size: number
  has_next: boolean
  since_days: number
}

type BusinessCallAdsCampaignResponse = {
  campaign: BusinessCallAdCampaign | null
}

type BusinessTeamResponse = {
  results: BusinessTeamMember[]
}

type BusinessOffersResponse = {
  results: BusinessOffer[]
  now?: string
}

type BusinessSpecialHoursResponse = {
  results: BusinessSpecialHour[]
}

type BusinessBookingListResponse = {
  results: BusinessBookingItem[]
}

type BusinessBookingNotificationsResponse = {
  results: BusinessBookingNotification[]
}

type MeBusinessesResponse = {
  results: MeBusinessMembership[]
}

type SponsoredCallAdsResponse = {
  results: SponsoredCallAdBusiness[]
}

type CallPricingTierListResponse = {
  results: CallPricingTier[]
}

export type DiscoverBusinessesResponse = {
  results: BusinessProfile[]
  page: number
  page_size: number
  has_next: boolean
}

export type DiscoverBusinessesParams = {
  q?: string
  cat?: string
  open_now?: boolean
  bookable_today?: boolean
  has_booking?: boolean
  sort?: 'newest' | 'reputation' | 'alphabetical' | 'soonest' | 'distance' | 'rating'
  lat?: number
  lng?: number
  page?: number
  page_size?: number
}

export async function createBusiness(payload: CreateBusinessPayload): Promise<BusinessProfile> {
  const { data } = await api.post<BusinessProfile>('/api/businesses/', payload)
  return data
}

export async function getBusinessBookingSettings(slug: string): Promise<BusinessBookingSettings> {
  const { data } = await api.get<BusinessBookingSettings>(`/api/businesses/${encodeURIComponent(slug)}/booking/settings/`)
  return data
}

export async function updateBusinessBookingSettings(
  slug: string,
  payload: Partial<Pick<BusinessBookingSettings, 'timezone' | 'requires_deposit' | 'deposit_type' | 'deposit_amount_cents' | 'deposit_percent_bps' | 'currency' | 'slot_duration_minutes' | 'buffer_minutes' | 'lead_time_minutes' | 'booking_window_days' | 'max_bookings_per_day' | 'allow_customer_cancel' | 'allow_customer_reschedule' | 'cancel_cutoff_minutes' | 'reschedule_cutoff_minutes' | 'is_enabled'>>,
): Promise<BusinessBookingSettings> {
  const { data } = await api.patch<BusinessBookingSettings>(`/api/businesses/${encodeURIComponent(slug)}/booking/settings/`, payload)
  return data
}

export async function listBusinessUpcomingBookings(slug: string, days = 14): Promise<BusinessBookingItem[]> {
  const { data } = await api.get<BusinessBookingListResponse>(`/api/businesses/${encodeURIComponent(slug)}/booking/manage/upcoming/`, {
    params: { days },
  })
  return Array.isArray(data?.results) ? data.results : []
}

export async function cancelBusinessBooking(bookingPublicId: string): Promise<BusinessBookingItem> {
  const { data } = await api.post<BusinessBookingItem>(`/api/booking/${encodeURIComponent(bookingPublicId)}/cancel/`, {})
  return data
}

export async function cancelBusinessBookingWithReason(bookingPublicId: string, reason: string): Promise<BusinessBookingItem> {
  const { data } = await api.post<BusinessBookingItem>(`/api/booking/${encodeURIComponent(bookingPublicId)}/cancel/`, { reason })
  return data
}

export async function rescheduleBusinessBooking(
  bookingPublicId: string,
  newStart: string,
  timezone?: string,
): Promise<BusinessBookingItem> {
  const payload: { new_start: string; timezone?: string } = { new_start: newStart }
  if (timezone) payload.timezone = timezone
  const { data } = await api.post<BusinessBookingItem>(`/api/booking/${encodeURIComponent(bookingPublicId)}/reschedule/`, payload)
  return data
}

export async function refundBusinessBooking(bookingPublicId: string): Promise<{ ok: boolean; booking_public_id: string; refund_id: string; refund_status: string; payment_status: string }> {
  const { data } = await api.post<{ ok: boolean; booking_public_id: string; refund_id: string; refund_status: string; payment_status: string }>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/refund/`,
    {},
  )
  return data
}

export async function resendBusinessBookingConfirm(bookingPublicId: string): Promise<{ ok: boolean; enqueued: boolean }> {
  const { data } = await api.post<{ ok: boolean; enqueued: boolean }>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/notify/confirm/`,
    {},
  )
  return data
}

export async function listBusinessBookingNotifications(bookingPublicId: string, limit = 20): Promise<BusinessBookingNotification[]> {
  const { data } = await api.get<BusinessBookingNotificationsResponse>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/notifications/`,
    { params: { limit } },
  )
  return Array.isArray(data?.results) ? data.results : []
}

function parseFilenameFromDisposition(disposition: string | undefined, fallback: string): string {
  const raw = String(disposition || '')
  const match = raw.match(/filename="?([^";]+)"?/i)
  return match?.[1] || fallback
}

export async function downloadBusinessBookingIcs(bookingPublicId: string, kind: 'request' | 'cancel' = 'request'): Promise<void> {
  const resp = await api.get(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/ics/`,
    { params: { kind }, responseType: 'blob' },
  )
  const contentType = String(resp.headers?.['content-type'] || 'text/calendar; charset=utf-8')
  const filename = parseFilenameFromDisposition(resp.headers?.['content-disposition'], `booking-${bookingPublicId}.ics`)
  const blob = new Blob([resp.data], { type: contentType })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

export async function updateBusiness(slug: string, payload: UpdateBusinessPayload): Promise<BusinessProfile> {
  const { data } = await api.patch<BusinessProfile>(`/api/businesses/${encodeURIComponent(slug)}/`, payload)
  return data
}

export async function myBusinesses(): Promise<BusinessProfile[]> {
  try {
    const { data } = await api.get<MineBusinessesResponse>('/api/businesses/mine/')
    return Array.isArray(data?.results) ? data.results : []
  } catch (error: any) {
    if (error?.response?.status === 404) return []
    throw error
  }
}

export async function publicBusiness(slug: string): Promise<BusinessProfile> {
  const { data } = await publicApi.get<BusinessProfile>(`/api/businesses/${encodeURIComponent(slug)}/`)
  return data
}

export async function listBusinessHours(slug: string): Promise<BusinessHoursGrouped> {
  const { data } = await publicApi.get<BusinessHoursGrouped>(`/api/businesses/${encodeURIComponent(slug)}/hours/`)
  return data
}

export async function getBusinessHoursManage(slug: string): Promise<BusinessHoursGrouped> {
  const { data } = await api.get<BusinessHoursGrouped>(`/api/businesses/${encodeURIComponent(slug)}/hours/manage/`)
  return data
}

export async function updateBusinessHours(slug: string, payload: BusinessHoursManagePayload): Promise<BusinessHoursGrouped> {
  const { data } = await api.post<BusinessHoursGrouped>(`/api/businesses/${encodeURIComponent(slug)}/hours/manage/`, payload)
  return data
}

export async function listBusinessSpecialHours(slug: string): Promise<BusinessSpecialHour[]> {
  const { data } = await publicApi.get<BusinessSpecialHoursResponse>(`/api/businesses/${encodeURIComponent(slug)}/special-hours/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function getBusinessSpecialHoursManage(slug: string): Promise<BusinessSpecialHour[]> {
  const { data } = await api.get<BusinessSpecialHoursResponse>(`/api/businesses/${encodeURIComponent(slug)}/special-hours/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function upsertBusinessSpecialHour(
  slug: string,
  payload: { date: string; is_closed: boolean; opens_at?: string | null; closes_at?: string | null; note?: string },
): Promise<BusinessSpecialHour> {
  const { data } = await api.post<BusinessSpecialHour>(`/api/businesses/${encodeURIComponent(slug)}/special-hours/manage/`, payload)
  return data
}

export async function deleteBusinessSpecialHour(slug: string, id: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/special-hours/manage/${id}/`)
}

export async function listBusinessOffers(slug: string): Promise<BusinessOffer[]> {
  const { data } = await publicApi.get<BusinessOffersResponse>(`/api/businesses/${encodeURIComponent(slug)}/offers/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function listBusinessOffersManage(slug: string): Promise<BusinessOffer[]> {
  const { data } = await api.get<BusinessOffersResponse>(`/api/businesses/${encodeURIComponent(slug)}/offers/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function createBusinessOffer(slug: string, payload: UpsertBusinessOfferPayload): Promise<BusinessOffer> {
  const { data } = await api.post<BusinessOffer>(`/api/businesses/${encodeURIComponent(slug)}/offers/manage/`, payload)
  return data
}

export async function updateBusinessOffer(slug: string, offerId: number, payload: UpsertBusinessOfferPayload): Promise<BusinessOffer> {
  const { data } = await api.patch<BusinessOffer>(`/api/businesses/${encodeURIComponent(slug)}/offers/manage/${offerId}/`, payload)
  return data
}

export async function deleteBusinessOffer(slug: string, offerId: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/offers/manage/${offerId}/`)
}

export async function businessActionsSummary(slug: string): Promise<BusinessActionsSummary> {
  const { data } = await publicApi.get<BusinessActionsSummary>(`/api/businesses/${encodeURIComponent(slug)}/actions/summary/`)
  return data
}

export async function likeBusiness(slug: string): Promise<BusinessLikeResponse> {
  const { data } = await api.post<BusinessLikeResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/like/`)
  return data
}

export async function unlikeBusiness(slug: string): Promise<BusinessLikeResponse> {
  const { data } = await api.delete<BusinessLikeResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/like/`)
  return data
}

export async function saveBusiness(slug: string): Promise<BusinessSaveResponse> {
  const { data } = await api.post<BusinessSaveResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/save/`)
  return data
}

export async function unsaveBusiness(slug: string): Promise<BusinessSaveResponse> {
  const { data } = await api.delete<BusinessSaveResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/save/`)
  return data
}

export async function shareBusiness(slug: string): Promise<BusinessShareResponse> {
  const { data } = await publicApi.post<BusinessShareResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/share/`)
  return data
}

export async function followBusiness(slug: string): Promise<BusinessFollowResponse> {
  const { data } = await api.post<BusinessFollowResponse>(`/api/businesses/${encodeURIComponent(slug)}/follow/`)
  return data
}

export async function unfollowBusiness(slug: string): Promise<BusinessFollowResponse> {
  const { data } = await api.delete<BusinessFollowResponse>(`/api/businesses/${encodeURIComponent(slug)}/follow/`)
  return data
}

export async function businessMetricsSummary(slug: string): Promise<BusinessMetricsSummary> {
  const { data } = await publicApi.get<BusinessMetricsSummary>(`/api/businesses/${encodeURIComponent(slug)}/metrics/summary/`)
  return data
}

export async function sendBusinessGift(slug: string, payload: BusinessGiftSendPayload): Promise<BusinessGiftSendResponse> {
  const { data } = await api.post<BusinessGiftSendResponse>(`/api/businesses/${encodeURIComponent(slug)}/actions/gift/`, payload)
  return data
}

export async function listBusinessComments(
  slug: string,
  params: { page?: number; page_size?: number } = {},
): Promise<BusinessCommentsResponse> {
  const { data } = await publicApi.get<BusinessCommentsResponse>(`/api/businesses/${encodeURIComponent(slug)}/comments/`, { params })
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    page: Number(data?.page || params.page || 1),
    page_size: Number(data?.page_size || params.page_size || 20),
    has_next: Boolean(data?.has_next),
  }
}

export async function createBusinessComment(slug: string, payload: { body: string }): Promise<BusinessComment> {
  const { data } = await api.post<BusinessComment>(`/api/businesses/${encodeURIComponent(slug)}/comments/`, payload)
  return data
}

export async function createBoost(slug: string, payload: CreateBusinessBoostPayload): Promise<BusinessBoost> {
  const { data } = await api.post<BusinessBoost>(`/api/businesses/${encodeURIComponent(slug)}/boosts/`, payload)
  return data
}

export async function listBoosts(slug: string): Promise<BusinessBoost[]> {
  const { data } = await api.get<BusinessBoostsResponse>(`/api/businesses/${encodeURIComponent(slug)}/boosts/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function cancelBoost(slug: string, boostId: number): Promise<BusinessBoost> {
  const { data } = await api.patch<BusinessBoost>(`/api/businesses/${encodeURIComponent(slug)}/boosts/${boostId}/`, {
    action: 'cancel',
  })
  return data
}

export async function getCallAdsCampaign(slug: string): Promise<BusinessCallAdCampaign | null> {
  const { data } = await api.get<BusinessCallAdsCampaignResponse>(`/api/businesses/${encodeURIComponent(slug)}/call-ads/campaign/`)
  return data?.campaign || null
}

export async function createCallAdsCampaign(slug: string, payload: UpsertBusinessCallAdCampaignPayload): Promise<BusinessCallAdCampaign> {
  const { data } = await api.post<BusinessCallAdCampaign>(`/api/businesses/${encodeURIComponent(slug)}/call-ads/campaign/`, payload)
  return data
}

export async function updateCallAdsCampaign(slug: string, payload: UpsertBusinessCallAdCampaignPayload): Promise<BusinessCallAdCampaign> {
  const { data } = await api.patch<BusinessCallAdCampaign>(`/api/businesses/${encodeURIComponent(slug)}/call-ads/campaign/`, payload)
  return data
}

export async function boostedBusinesses(): Promise<BusinessProfile[]> {
  try {
    const { data } = await publicApi.get<MineBusinessesResponse>('/api/pages/boosts/active/')
    return Array.isArray(data?.results) ? data.results : []
  } catch (error: any) {
    if (error?.response?.status === 404) return []
    throw error
  }
}

export async function sponsoredCallAds(params: { cat?: string; q?: string; limit?: number } = {}): Promise<SponsoredCallAdBusiness[]> {
  try {
    const { data } = await publicApi.get<SponsoredCallAdsResponse>('/api/pages/call-ads/sponsored/', { params })
    return Array.isArray(data?.results) ? data.results : []
  } catch (error: any) {
    if (error?.response?.status === 404) return []
    throw error
  }
}

export async function discoverBusinesses(params: DiscoverBusinessesParams = {}): Promise<DiscoverBusinessesResponse> {
  const { data } = await publicApi.get<DiscoverBusinessesResponse>('/api/pages/discover/', { params })
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    page: Number(data?.page || params.page || 1),
    page_size: Number(data?.page_size || params.page_size || 12),
    has_next: Boolean(data?.has_next),
  }
}

export async function callClick(
  slug: string,
  source: 'READER' | 'CARD' | 'OTHER',
  anonId?: string,
  context?: { cat?: string; q?: string; placement_context?: string },
): Promise<BusinessCallClickResponse> {
  const payload: Record<string, any> = { source }
  if (anonId) payload.anon_id = anonId
  if (context?.cat) payload.cat = context.cat
  if (context?.q) payload.q = context.q
  if (context?.placement_context) payload.placement_context = context.placement_context
  const { data } = await publicApi.post<BusinessCallClickResponse>(`/api/businesses/${encodeURIComponent(slug)}/call-click/`, payload)
  return data
}

export async function callSummary(slug: string, sinceDays = 30): Promise<BusinessCallsSummary> {
  const { data } = await api.get<BusinessCallsSummary>(`/api/businesses/${encodeURIComponent(slug)}/calls/summary/`, {
    params: { since_days: sinceDays },
  })
  return data
}

export async function callEvents(slug: string, sinceDays = 30, page = 1, pageSize = 50): Promise<BusinessCallEventsResponse> {
  const { data } = await api.get<BusinessCallEventsResponse>(`/api/businesses/${encodeURIComponent(slug)}/calls/events/`, {
    params: { since_days: sinceDays, page, page_size: pageSize },
  })
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    page: Number(data?.page || page),
    page_size: Number(data?.page_size || pageSize),
    has_next: Boolean(data?.has_next),
    since_days: Number(data?.since_days || sinceDays),
  }
}

export async function exportCallsCsv(slug: string, sinceDays = 30): Promise<Blob> {
  const { data } = await api.get(`/api/businesses/${encodeURIComponent(slug)}/calls/export.csv`, {
    params: { since_days: sinceDays },
    responseType: 'blob',
  })
  return data as Blob
}

export async function adminSummary(slug: string, sinceDays = 30): Promise<BusinessAdminSummary> {
  const { data } = await api.get<BusinessAdminSummary>(`/api/businesses/${encodeURIComponent(slug)}/admin/summary/`, {
    params: { since_days: sinceDays },
  })
  return data
}

export async function callAdsSummary(slug: string, sinceDays = 30): Promise<BusinessCallAdsSummary> {
  const { data } = await api.get<BusinessCallAdsSummary>(`/api/businesses/${encodeURIComponent(slug)}/call-ads/summary/`, {
    params: { since_days: sinceDays },
  })
  return data
}

export async function callAdsEvents(slug: string, sinceDays = 30, page = 1, pageSize = 25): Promise<SponsoredLeadEventsResponse> {
  const { data } = await api.get<SponsoredLeadEventsResponse>(`/api/businesses/${encodeURIComponent(slug)}/call-ads/events/`, {
    params: { since_days: sinceDays, page, page_size: pageSize },
  })
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    page: Number(data?.page || page),
    page_size: Number(data?.page_size || pageSize),
    has_next: Boolean(data?.has_next),
    since_days: Number(data?.since_days || sinceDays),
  }
}

export async function exportCallAdsCsv(slug: string, sinceDays = 30): Promise<Blob> {
  const { data } = await api.get(`/api/businesses/${encodeURIComponent(slug)}/call-ads/export.csv`, {
    params: { since_days: sinceDays },
    responseType: 'blob',
  })
  return data as Blob
}

export async function getCallSettings(slug: string): Promise<CallSettings> {
  const { data } = await api.get<CallSettings>(`/api/businesses/${encodeURIComponent(slug)}/call-settings/`)
  return data
}

export async function updateCallSettings(slug: string, payload: UpdateCallSettingsPayload): Promise<CallSettings> {
  const { data } = await api.patch<CallSettings>(`/api/businesses/${encodeURIComponent(slug)}/call-settings/`, payload)
  return data
}

export async function getCallPricingTiers(): Promise<CallPricingTier[]> {
  const { data } = await publicApi.get<CallPricingTierListResponse>('/api/call-pricing/tiers/')
  return Array.isArray(data?.results) ? data.results : []
}

export async function listTeam(slug: string): Promise<BusinessTeamMember[]> {
  const { data } = await api.get<BusinessTeamResponse>(`/api/businesses/${encodeURIComponent(slug)}/team/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function listPublicTeam(slug: string): Promise<BusinessTeamMember[]> {
  const { data } = await publicApi.get<BusinessTeamResponse>(`/api/businesses/${encodeURIComponent(slug)}/team/public/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function addTeamMember(slug: string, payload: AddTeamMemberPayload): Promise<BusinessTeamMember> {
  const { data } = await api.post<BusinessTeamMember>(`/api/businesses/${encodeURIComponent(slug)}/team/`, payload)
  return data
}

export async function updateTeamMemberRole(slug: string, memberId: number, role: BusinessTeamMember['role']): Promise<BusinessTeamMember> {
  const { data } = await api.patch<BusinessTeamMember>(`/api/businesses/${encodeURIComponent(slug)}/team/${memberId}/`, { role })
  return data
}

export async function updateTeamMember(
  slug: string,
  memberId: number,
  payload: Partial<Pick<BusinessTeamMember, 'role' | 'is_hidden_by_business'>>,
): Promise<BusinessTeamMember> {
  const { data } = await api.patch<BusinessTeamMember>(`/api/businesses/${encodeURIComponent(slug)}/team/${memberId}/`, payload)
  return data
}

export async function removeTeamMember(slug: string, memberId: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/team/${memberId}/`)
}

export async function myLinkedBusinesses(): Promise<MeBusinessMembership[]> {
  const { data } = await api.get<MeBusinessesResponse>('/api/me/businesses/')
  return Array.isArray(data?.results) ? data.results : []
}

export async function linkMeToBusiness(payload: {
  business_slug: string
  role?: 'EMPLOYEE' | 'EDITOR'
  is_public?: boolean
}): Promise<MeBusinessMembership> {
  const { data } = await api.post<MeBusinessMembership>('/api/me/businesses/link/', payload)
  return data
}

export async function updateMyBusinessLink(
  membershipId: number,
  payload: { role?: 'EMPLOYEE' | 'EDITOR'; is_public?: boolean },
): Promise<MeBusinessMembership> {
  const { data } = await api.patch<MeBusinessMembership>(`/api/me/businesses/${membershipId}/`, payload)
  return data
}

export async function unlinkMyBusiness(membershipId: number): Promise<void> {
  await api.delete(`/api/me/businesses/${membershipId}/`)
}

export async function listBusinessReviews(
  slug: string,
  params: {
    page?: number
    page_size?: number
    sort?: 'newest' | 'highest' | 'lowest'
  } = {},
): Promise<BusinessReviewsResponse> {
  const { data } = await publicApi.get<BusinessReviewsResponse>(`/api/businesses/${encodeURIComponent(slug)}/reviews/`, { params })
  return {
    summary: data?.summary || {
      avg_rating: '0.0',
      total: 0,
      counts: { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 },
    },
    results: Array.isArray(data?.results) ? data.results : [],
    page: Number(data?.page || params.page || 1),
    page_size: Number(data?.page_size || params.page_size || 10),
    has_next: Boolean(data?.has_next),
    sort: (data?.sort as 'newest' | 'highest' | 'lowest' | undefined) || params.sort || 'newest',
  }
}

export async function createBusinessReview(
  slug: string,
  payload: { rating: number; title?: string; body?: string },
): Promise<BusinessReview> {
  const { data } = await api.post<BusinessReview>(`/api/businesses/${encodeURIComponent(slug)}/reviews/`, payload)
  return data
}
